import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import sortBy from 'lodash/sortBy';
import isEmpty from 'lodash/isEmpty';
import { LIST_PROGRAM_OPTIONS } from 'core/api/apollo/queries';

import { useProgramContext } from 'core/api/programContext';

const useListPrograms = () => {
  const { selectedProgram, mealBundle, selectedDiet, setSelectedDiet } = useProgramContext();

  // eslint-disable-next-line no-console
  console.log('selectedProgram--', selectedProgram);

  const {
    loading,
    data: {
      listPrepaidPrograms: programs = [],
      listProgramSizes: programSizes = [],
      listProgramSchedules: programSchedules = [],
      listProgramMealTypes: programMealTypes = [],
    } = {},
  } = useQuery(LIST_PROGRAM_OPTIONS);

  const signatureProgramList = sortBy(programs, 'position').filter((item) => {
    const itemName = item.name.toLowerCase();
    return !itemName.includes('haute mom') && !itemName.includes('maison')
      && !itemName.includes('sustain') && !itemName.includes('b/l') && !itemName.includes('peak');
  }).map((item) => ({
    ...item,
    name: item.name,
    internalName: item.name.toLowerCase().replace(/\s+/g, ''),
  }));

  const blSignatureProgramList = sortBy(programs, 'position').filter((item) => {
    const itemName = item.name.toLowerCase();
    return !itemName.includes('haute mom') && !itemName.includes('maison')
      && !itemName.includes('sustain') && !itemName.includes('peak')
      && itemName.startsWith('b/l');
  }).map((item) => ({
    ...item,
    name: item.name.replace('B/L', '').trim(),
    internalName: item.name.replace('B/L', '').trim().toLowerCase().replace(/\s+/g, ''),
  }));

  const sustainProgramList = sortBy(programs, 'position').filter((item) => {
    const itemName = item.name.toLowerCase();
    return itemName.includes('sustain');
  }).map((item) => ({
    ...item,
    name: item.name,
    internalName: item.name.replace('Sustain :', '').trim().toLowerCase().replace(/\s+/g, ''),
  }));

  const peakProgramList = sortBy(programs, 'position').filter((item) => {
    const itemName = item.name.toLowerCase();
    return itemName.includes('peak');
  }).map((item) => ({
    ...item,
    name: item.name,
    internalName: item.name.replace('Peak :', '').trim().toLowerCase().replace(/\s+/g, ''),
  }));

  const programMealPortions = selectedProgram === 'peak' ?
    sortBy(programSizes, 'position').filter((option) => option?.name !== 'Standard') :
    sortBy(programSizes, 'position').filter((option) => option?.name !== 'X-Large');

  // const filterMealsByProgram = selectedProgram === 'sustain' ? sustainProgramList
  //   : selectedProgram === 'peak' ? peakProgramList : signatureProgramList;
  const filterMealsByProgram = useMemo(() => {
    return selectedProgram === 'sustain' ? sustainProgramList
      : selectedProgram === 'peak' ? peakProgramList : signatureProgramList;
  }, [selectedProgram, sustainProgramList, peakProgramList, signatureProgramList]);

  useEffect(() => {
    if (loading || filterMealsByProgram.length === 0) return;
    // Only sync selectedDiet when it doesn't already match an item in the current list by id
    const alreadyInList = filterMealsByProgram.some((e) => e.id === selectedDiet?.id);
    if (alreadyInList) return;
    const currentDiet = filterMealsByProgram.find((e) => e.internalName === selectedDiet?.internalName);
    if (currentDiet) {
      setSelectedDiet(currentDiet);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, filterMealsByProgram]);


  const altertedProgramMealTypes = isEmpty(programMealTypes) ? [] : programMealTypes.map((mealType) => {
    if (mealType.name === 'Breakfast') {
      return { ...mealType, description: 'Breakfast | Lunch | Dinner (No snacks)' };
    }
    if (mealType.name === 'Without Breakfast') {
      return { ...mealType, description: 'Lunch | Dinner (No snacks)' };
    }
    if (mealType.name === 'Without Dinner') {
      return { ...mealType, description: 'Breakfast | Lunch (No snacks)' };
    }
  });

  const programMealTypesList = selectedProgram === 'signature' ? programMealTypes : altertedProgramMealTypes;

  const selectedProgramSizeOptions = selectedProgram === 'peak' ?
    sortBy(programSizes, 'position').filter((option) => option?.name !== 'Standard') :
    sortBy(programSizes, 'position').filter((sizeOption) => sizeOption.position !== 3);


  const sustainDaysOfFood = mealBundle?.name === 'Breakfast'
    ? programSchedules.filter((schedule) => ['3 Days', '4 Days'].includes(schedule.name))
    : mealBundle?.name === 'Without Breakfast'
    ? programSchedules.filter((schedule) => ['3 Days', '4 Days', '5 Days'].includes(schedule.name))
    : mealBundle?.name === 'Without Dinner'
    ? programSchedules.filter((schedule) => ['5 Days'].includes(schedule.name))
    : [];

  const peakDaysOfFood = mealBundle?.name === 'Breakfast'
    ? programSchedules.filter((schedule) => ['3 Days', '4 Days'].includes(schedule.name))
    : mealBundle?.name === 'Without Breakfast'
    ? programSchedules.filter((schedule) => ['3 Days', '4 Days', '5 Days'].includes(schedule.name))
    : mealBundle?.name === 'Without Dinner'
    ? programSchedules.filter((schedule) => ['5 Days'].includes(schedule.name))
    : [];

  const signatureDaysOfFood = mealBundle?.name === 'Breakfast'
    ? programSchedules.filter((schedule) => ['1 Day', '2 Days', '3 Days', '4 Days'].includes(schedule.name))
    : mealBundle?.name === 'Without Breakfast'
    ? programSchedules.filter((schedule) => ['5 Days'].includes(schedule.name))
    : mealBundle?.name === 'Without Dinner'
    ? programSchedules.filter((schedule) => ['5 Days'].includes(schedule.name))
    : [];

  const daysOfFoodList = selectedProgram === 'sustain' ? sustainDaysOfFood
    : selectedProgram === 'peak' ? peakDaysOfFood : signatureDaysOfFood;


  return {
    loading,
    daysOfFoodList,
    programs,
    programSizes,
    programSchedules,
    programMealTypes,
    signatureProgramList,
    blSignatureProgramList,
    sustainProgramList,
    peakProgramList,
    programMealPortions,
    filterMealsByProgram,
    programMealTypesList,
    selectedProgramSizeOptions,
  };
};

export default useListPrograms;
